// @owners { team: patients }
import { ActionSheetContext } from '@alto/design-system';
import React, { useContext } from 'react';
import { setOnboardingVerificationChannel, setOnboardingVerificationRequired } from '~shared/actions/verifications';
import {
  getOnboardingVerificationOptions,
  getOnboardingVerificationUuid,
} from '~shared/features/onboarding/selectors/getOnboardingVerification';
import { useStartVerification } from '~shared/queries/query-keys/verifications';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { VerificationBlockedActionSheet } from '~web/features/onboarding/components/VerificationBlockedActionSheet';
import {
  type Values,
  VerificationOptionsActionSheet,
} from '~web/features/onboarding/components/VerificationOptionsActionSheet';
import { VerificationSingleOptionActionSheet } from '~web/features/onboarding/components/VerificationSingleOptionActionSheet';

type Props = {
  readonly resend?: boolean;
};

export const VerificationActionSheet = ({ resend = false }: Props) => {
  const dispatch = useDispatchShared();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const uuid = useSelectorShared(getOnboardingVerificationUuid);
  const options = useSelectorShared(getOnboardingVerificationOptions);
  const { mutateAsync: startVerification, isPending: loading, error } = useStartVerification();

  const onSubmit = async ({ channel: comms_channel }: Values) => {
    try {
      await startVerification({
        uuid,
        reason: 'duplicate_account',
        comms_channel,
      });
    } catch {
      // the mutation error renders in the action sheet below
      return;
    }

    closeActionSheet();
    dispatch(setOnboardingVerificationRequired());
    dispatch(setOnboardingVerificationChannel(comms_channel));
  };

  if (options.length === 0) {
    return <VerificationBlockedActionSheet />;
  }

  if (options.length === 1) {
    const option = options[0];
    return (
      <VerificationSingleOptionActionSheet
        error={error}
        loading={loading}
        onSubmit={onSubmit}
        option={option}
        resend={resend}
      />
    );
  }

  return (
    <VerificationOptionsActionSheet
      error={error}
      loading={loading}
      onSubmit={onSubmit}
      options={options}
      resend={resend}
    />
  );
};
